import { Injectable } from '@angular/core';
import { filter, Observable, switchMap, take, of, Subject, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import {
  Credential,
  ConfiguracionGeneral,
  ConfiguracionGeneralForm,
} from '@core/models';
import { environment } from '@environments/environment';
import { ResponseSuccess } from '@interfaces/response-success';
import { AuthenticationState } from '@store/reducers/authentication.reducer';
import { selectCredential } from '@store/selectors/authentication.selectors';
import {
  transformToBusinessSettingUpdate,
  transformDropdown,
  toCapitalize,
} from '@core/helpers';
import { ResponseObjectSuccess } from '@interfaces/response-success';
import { MenuItem } from 'primeng/api';
import {
  BusinessSettingEnum,
  DayWeekEnglishEnum,
  DayWeekSpanishEnum,
} from '@core/enums';
import { MenuItemExtended } from '@shared/components/tab-menu/interfaces';
import { TranslationHelper } from '@core/helpers'
import { selectBusinessSetting } from '@store/selectors';

@Injectable({
  providedIn: 'root',
})
export class BusinessSettingService {
  credential$: Observable<Credential | null>;
  businessSetting$: Observable<ConfiguracionGeneral | null>;

  private changeTabSource = new Subject<MenuItemExtended|null>();
  changeTab$ = this.changeTabSource.asObservable();

  private channelsSource = new Subject<string[]>();
  channels$ = this.channelsSource.asObservable();

  private creationCompletedSource = new Subject<void>();
  creationCompleted$ = this.creationCompletedSource.asObservable();

  private updateCompletedSource = new Subject<void>();
  updateCompleted$ = this.updateCompletedSource.asObservable();

  private closeDialogSource = new Subject<void>();
  closeDialog$ = this.closeDialogSource.asObservable();

  cancelPendingRequests$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private store: Store<AuthenticationState>,
    private translationHelper: TranslationHelper
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.businessSetting$ = this.store.select(selectBusinessSetting);
  }

  creationCompleted(): void {
    this.creationCompletedSource.next();
    this.closeDialog();
  }
  
  updateCompleted(): void {
    this.updateCompletedSource.next();
    this.closeDialog();
  }

  closeDialog(): void {
    this.closeDialogSource.next();
  }

  changeTab(event: MenuItemExtended): void {
    setTimeout(() => this.changeTabSource.next(event));
  }

  channels(channels?: string[]): void {
    this.channelsSource.next(channels ?? []);
  }

  getDateFormSettingDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: BusinessSettingEnum.FormatDateEnglish,
            label: BusinessSettingEnum.FormatDateEnglish,
          },
          {
            value: BusinessSettingEnum.FormatDateSpanish,
            label: BusinessSettingEnum.FormatDateSpanish,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getCurrencySymbolDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: BusinessSettingEnum.CurrencyEuro,
            label: BusinessSettingEnum.CurrencyEuro,
          },
          {
            value: BusinessSettingEnum.CurrencyDollarLabel,
            label: BusinessSettingEnum.CurrencyDollar,
          },
          {
            value: BusinessSettingEnum.CurrencyPesoArgentinoLabel,
            label: BusinessSettingEnum.CurrencyPesoArgentinoLabel,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getPrintActionInPosDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            label: this.translationHelper.getTranslate(BusinessSettingEnum.PrintActionKitchenAndCheckLabel),
            value: BusinessSettingEnum.PrintActionKitchenAndCheckLabel,
          },
          {
            label: this.translationHelper.getTranslate(BusinessSettingEnum.PrintActionOnlyKitchenLabel),
            value: BusinessSettingEnum.PrintActionOnlyKitchenLabel,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getNameFormatDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.LastNameFormatLabel),
            label: BusinessSettingEnum.LastNameFormat,
          },
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.FirstNameFormatLabel),
            label: BusinessSettingEnum.FirstNameFormat,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getHourFormatDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.HourFormatAmPm),
            label: BusinessSettingEnum.HourFormatAmPm,
          },
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.HourFormatMilitaryLabel),
            label: BusinessSettingEnum.HourFormatMilitary,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getSendActionInPosDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.ActionSendAndNewLabel),
            label: BusinessSettingEnum.ActionSendAndNew,
          },
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.ActionSendQuitLabel),
            label: BusinessSettingEnum.ActionSendQuit,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getPrecisionDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          { value: BusinessSettingEnum.PrecisionTwoLabel, label: 2 },
          { value: BusinessSettingEnum.PrecisionThreeLabel, label: 3 },
          { value: BusinessSettingEnum.PrecisionEightLabel, label: 8 },
        ],
        'label',
        'value'
      )
    );
  }

  getDefaultSearchCriteriaDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: BusinessSettingEnum.DefaultSearchCriterialId,
            label: BusinessSettingEnum.DefaultSearchCriterialId,
          },
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.DefaultSearchCriterialPhoneLabel),
            label: BusinessSettingEnum.DefaultSearchCriterialPhone,
          },
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.DefaultSearchCriterialNameLabel),
            label: BusinessSettingEnum.DefaultSearchCriterialName,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getTipDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.TipPaidOutLabel),
            label: BusinessSettingEnum.TipPaidOut,
          },
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.TipRetainedLabel),
            label: BusinessSettingEnum.TipRetained,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getGratuityDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.GratuityPaidOutLabel),
            label: BusinessSettingEnum.GratuityPaidOut,
          },
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.GratuityRetainedLabel),
            label: BusinessSettingEnum.GratuityRetained,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getDriverCompensationDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.DriverCompensationPaidOutLabel),
            label: BusinessSettingEnum.DriverCompensationPaidOut,
          },
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.DriverCompensationRetainedLabel),
            label: BusinessSettingEnum.DriverCompensationRetained,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getStartOfWeekDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: this.translationHelper.getTranslate(toCapitalize(DayWeekEnglishEnum.Monday)),
            label: DayWeekSpanishEnum.Lunes,
          },
          {
            value: this.translationHelper.getTranslate(toCapitalize(DayWeekEnglishEnum.Tuesday)),
            label: DayWeekSpanishEnum.Martes,
          },
          {
            value: this.translationHelper.getTranslate(toCapitalize(DayWeekEnglishEnum.Wednesday)),
            label: DayWeekSpanishEnum.Miercoles,
          },
          {
            value: this.translationHelper.getTranslate(toCapitalize(DayWeekEnglishEnum.Thursday)),
            label: DayWeekSpanishEnum.Jueves,
          },
          {
            value: this.translationHelper.getTranslate(toCapitalize(DayWeekEnglishEnum.Friday)),
            label: DayWeekSpanishEnum.Viernes,
          },
          {
            value: this.translationHelper.getTranslate(toCapitalize(DayWeekEnglishEnum.Saturday)),
            label: DayWeekSpanishEnum.Sabado,
          },
          {
            value: this.translationHelper.getTranslate(toCapitalize(DayWeekEnglishEnum.Sunday)),
            label: DayWeekSpanishEnum.Domingo,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getTypeDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.TypeDaily),
            label: BusinessSettingEnum.TypeDaily,
          },
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.TypeWeekly),
            label: BusinessSettingEnum.TypeWeekly,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getSalesAccountabilityDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.AccountabilityInitialLabel),
            label: BusinessSettingEnum.AccountabilityInitial,
          },
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.AccountabilityEndLabel),
            label: BusinessSettingEnum.AccountabilityEnd,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getPaymentAccountabilityDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.AccountabilityInitialLabel),
            label: BusinessSettingEnum.AccountabilityInitial,
          },
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.AccountabilityEndLabel),
            label: BusinessSettingEnum.AccountabilityEnd,
          },
        ],
        'label',
        'value'
      )
    );
  }

  geTipAccountabilityDropdown(): Observable<MenuItem[]> {
    return of(
      transformDropdown(
        [
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.AccountabilityInitialLabel),
            label: BusinessSettingEnum.AccountabilityInitial,
          },
          {
            value: this.translationHelper.getTranslate(BusinessSettingEnum.AccountabilityEndLabel),
            label: BusinessSettingEnum.AccountabilityEnd,
          },
        ],
        'label',
        'value'
      )
    );
  }

  getCurrencySymbol(): Observable<string> {
    return this.businessSetting$.pipe(
      filter(
        (businessSetting): businessSetting is ConfiguracionGeneral =>
          !!businessSetting &&
          !!businessSetting?.configuracionTienda
      ),
      switchMap((businessSetting) => {
        switch (businessSetting.configuracionTienda.simboloMoneda) {
          case BusinessSettingEnum.CurrencyEuro:
            return of('€');
          case BusinessSettingEnum.CurrencyDollar:
            return of('$');
          case BusinessSettingEnum.CurrencyPesoArgentinoLabel:
            return of('$');
          default:
            return of('$');
        }
      }),
      take(1)
    );
  }

  getPrecision(): Observable<number> {
    return this.businessSetting$.pipe(
      filter(
        (businessSetting): businessSetting is ConfiguracionGeneral =>
          !!businessSetting &&
          !!businessSetting?.configuracionTienda
      ),
      switchMap((businessSetting) => {
        return of(businessSetting?.configuracionTienda?.precisionDecimal ?? 2);
      }),
      take(1)
    );
  }

  getBusinessSettings({ id }: { id: string }): Observable<ResponseSuccess<ConfiguracionGeneral>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<ConfiguracionGeneral>>(
          `${environment.microConfiguraciones}/api/v1/configuraciongeneral/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      takeUntil(this.cancelPendingRequests$),
      take(1)
    );
  }

  create(
    businessSettingForm: ConfiguracionGeneralForm
  ): Observable<ResponseObjectSuccess<ConfiguracionGeneral>> {
    return this.credential$.pipe(
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.post<ResponseObjectSuccess<ConfiguracionGeneral>>(
          `${environment.microConfiguraciones}/api/v1/configuraciongeneral`,
          transformToBusinessSettingUpdate({
            ...businessSettingForm,
            localId: credential!.idTienda!,
          }),
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  cancelPendingRequests(): void {
    this.cancelPendingRequests$.next();
  }
}
